import { Icon } from '@buggy/shared'
import { Button, Flex, InputNumber } from 'antd'
import Input from 'antd/es/input/Input'
import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import CollaborativeItemTypeCascader, {
  type CollaborativeItemTypeCascaderProps,
} from '@/components/common/collaborative/collaborative-item-type-cascader'
import NumberPicker from '@/components/common/input/number-picker'
import useWorkspaceDollarSign from '@/hooks/use-workspace-dollar-sign'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Nullable } from '@/types/nullable'
import type { CollaborativeItem } from '@/types/schema/collaborative/collaborative-item'
import { PlatformShortcode } from '@/utils/convert-platform'

const DEFAULT_VALUE: CollaborativeItem = {
  platform_name: '',
  is_custom_platform: true,
  collaborative_item: '',
  is_custom_collaborative_item: true,
  cooperation_fee: 0,
  collaborative_items_count: 0,
}

interface CollaborativeItemInputProps {
  value?: CollaborativeItem
  onChange?: (value: CollaborativeItem) => void
  onDelete?: VoidFunction
  disabledItemTypeCascaderOptions?: CollaborativeItemTypeCascaderProps['disabledOptions']
}

const CollaborativeItemInput: FunctionComponent<
  CollaborativeItemInputProps
> = ({ value, onChange, onDelete, disabledItemTypeCascaderOptions }) => {
  const { formatMessage } = useIntl()
  const workspaceDollarSign = useWorkspaceDollarSign()

  return (
    <Flex gap={12}>
      <CollaborativeItemInputWrapper>
        {value?.is_custom_platform ? (
          <Input
            placeholder={formatMessage({
              id: 'general:column_title_collaboration_platform',
            })}
            size='small'
            value={value?.platform_name}
            onChange={(event) => {
              onChange?.({
                ...DEFAULT_VALUE,
                ...value,
                platform_name: event.target.value,
              })
            }}
          />
        ) : (
          <CollaborativeItemTypeCascader
            disabledOptions={disabledItemTypeCascaderOptions}
            value={
              value
                ? [
                    value?.platform_name,
                    value?.is_custom_collaborative_item
                      ? 'custom'
                      : value?.collaborative_item,
                  ]
                : undefined
            }
            onChange={([platform, itemType]) => {
              if (platform === PlatformShortcode.Other) {
                onChange?.({
                  ...DEFAULT_VALUE,
                  ...value,
                  platform_name: '',
                  is_custom_platform: true,
                  collaborative_item: '',
                  is_custom_collaborative_item: true,
                })
              } else {
                onChange?.({
                  ...DEFAULT_VALUE,
                  ...value,
                  platform_name: platform,
                  is_custom_platform: false,
                  ...(itemType === 'custom'
                    ? {
                        collaborative_item: '',
                        is_custom_collaborative_item: true,
                      }
                    : {
                        collaborative_item: itemType,
                        is_custom_collaborative_item: false,
                      }),
                })
              }
            }}
          />
        )}
        {value?.is_custom_collaborative_item && (
          <Input
            placeholder={formatMessage({
              id: 'general:column_title_collaboration_item_type',
            })}
            size='small'
            value={value.collaborative_item}
            onChange={(event) => {
              onChange?.({
                ...DEFAULT_VALUE,
                ...value,
                collaborative_item: event.target.value,
              })
            }}
          />
        )}
      </CollaborativeItemInputWrapper>
      <StyledNumberPicker
        min={1}
        size='small'
        value={value?.collaborative_items_count}
        onChange={(count) => {
          onChange?.({
            ...DEFAULT_VALUE,
            ...value,
            collaborative_items_count: count ?? 0,
          })
        }}
      />
      <Flex align='center' gap={6} style={{ minWidth: 220 }}>
        <FeeInput
          controls={false}
          min={0}
          placeholder={formatMessage({
            id: 'general:column_title_collaboration_compensation',
          })}
          prefix={workspaceDollarSign}
          size='small'
          value={value?.cooperation_fee}
          onChange={(cooperationFee: Nullable<number>): void => {
            onChange?.({
              ...DEFAULT_VALUE,
              ...value,
              cooperation_fee: cooperationFee ?? 0,
            })
          }}
        />
        {onDelete && (
          <DeleteButton
            icon={<Icon fontSize={16} name='x-circle' />}
            size='small'
            type='text'
            onClick={onDelete}
          />
        )}
      </Flex>
    </Flex>
  )
}

const CollaborativeItemInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  .ant-input,
  .ant-select {
    flex: 1 1 100px;
    width: 0;
  }
`

const StyledNumberPicker = styled(NumberPicker)`
  /* width: 58px; */

  &.ant-input-number-status-error:not(.ant-input-number-disabled) {
    border-left-color: var(--ant-color-border);
  }
`

const FeeInput = styled(InputNumber)`
  width: 100%;
`

const DeleteButton = styled(Button)`
  padding: 0;
`

export default CollaborativeItemInput
